import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Check, Phone, Mail, MapPin, Calendar } from "lucide-react";
import { BUSINESS } from "../data/content";
import { SectionLabel } from "./Section";
import { fadeUp, viewport } from "../lib/motion";

const TYPES = ["General Inquiry", "Catering", "Private Event"];

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    type: "General Inquiry",
    date: "",
    guests: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isEvent = form.type === "Catering" || form.type === "Private Event";

  const mailto = () => {
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Inquiry: ${form.type}`,
      isEvent ? `Event date: ${form.date}` : null,
      isEvent ? `Guests: ${form.guests}` : null,
      "",
      "Message:",
      form.message,
    ]
      .filter((x) => x !== null)
      .join("\n");
    return `mailto:${BUSINESS.email}?subject=${encodeURIComponent(
      `${form.type} — Detour Coffee Co.`
    )}&body=${encodeURIComponent(body)}`;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSent(true);
  };

  const field =
    "w-full rounded-xl border border-surface bg-background px-4 py-3 text-sm text-foreground placeholder:text-foreground/40 outline-none transition-colors focus:border-primary";

  return (
    <section id="contact" className="relative py-16 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* Left: intro + details */}
        <div>
          <SectionLabel>Get in Touch</SectionLabel>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
          >
            Let's plan something <span className="text-highlight">delicious.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-5 text-foreground/70 text-base sm:text-lg leading-relaxed max-w-md"
          >
            Questions, catering, or a private event in our space? Send a note and our team will get back to you within one business day.
          </motion.p>

          <div className="mt-9 space-y-4">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors" data-testid="contact-phone">
              <span className="h-10 w-10 rounded-xl bg-surface flex items-center justify-center text-primary"><Phone size={18} strokeWidth={1.6} /></span>
              {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 text-foreground hover:text-primary transition-colors" data-testid="contact-email-link">
              <span className="h-10 w-10 rounded-xl bg-surface flex items-center justify-center text-primary"><Mail size={18} strokeWidth={1.6} /></span>
              {BUSINESS.email}
            </a>
            <div className="flex items-center gap-3 text-foreground">
              <span className="h-10 w-10 rounded-xl bg-surface flex items-center justify-center text-primary"><MapPin size={18} strokeWidth={1.6} /></span>
              {BUSINESS.address}
            </div>
          </div>
        </div>

        {/* Right: form */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="rounded-3xl border border-surface bg-surface/30 p-6 sm:p-8 shadow-warm"
        >
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
                data-testid="contact-success"
              >
                <div className="mx-auto h-14 w-14 rounded-full bg-primary text-background flex items-center justify-center">
                  <Check size={26} strokeWidth={2} />
                </div>
                <h3 className="mt-5 text-2xl">Thank you, {form.name.split(" ")[0] || "friend"}!</h3>
                <p className="mt-2 text-foreground/65 text-sm max-w-sm mx-auto">
                  We've received your {form.type.toLowerCase()} request and will be in touch within one business day.
                </p>
                <a
                  href={mailto()}
                  data-testid="contact-mailto-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-highlight transition-colors"
                >
                  <Mail size={16} /> Also send it via email
                </a>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                exit={{ opacity: 0 }}
                className="space-y-4"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Name *</label>
                    <input required value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Email *</label>
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={field} data-testid="contact-email-input" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Phone</label>
                    <input value={form.phone} onChange={set("phone")} placeholder="(703) 000-0000" className={field} data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Inquiry Type *</label>
                    <select value={form.type} onChange={set("type")} className={field} data-testid="contact-type-select">
                      {TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <AnimatePresence>
                  {isEvent && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="grid sm:grid-cols-2 gap-4 overflow-hidden"
                      data-testid="contact-event-fields"
                    >
                      <div className="pt-0">
                        <label className="block text-xs font-semibold text-foreground/70 mb-1.5 flex items-center gap-1.5"><Calendar size={13} /> Event Date</label>
                        <input type="date" value={form.date} onChange={set("date")} className={field} data-testid="contact-date-input" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Guests</label>
                        <input type="number" min="1" value={form.guests} onChange={set("guests")} placeholder="e.g. 25" className={field} data-testid="contact-guests-input" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="block text-xs font-semibold text-foreground/70 mb-1.5">Message</label>
                  <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Tell us a little about what you're planning…" className={`${field} resize-none`} data-testid="contact-message-input" />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Send Message
                  <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="text-center text-xs text-foreground/45">We'll never share your details. Typical reply within 24 hours.</p>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
